import React, { useState, useEffect } from "react";
import { flow } from "lodash";
import { AVAILABILITY_TYPES } from "~/src/consts";
import { RotaButton } from "@teamrota/rota-design";
import updateMemberPreferences from "./graphql/update-member-preferences";
import {
  Container,
  Section,
  SectionTitle,
  ButtonContainer,
  StatusMessage
} from "./style";

import DaySelector from "./day-selector";
import WeeklyHours from "./weekly-hours";
import ToggleOption from "./toggle-option";

export const MemberPreferencesComponent = ({
  memberId,
  member,
  updateMemberPreferences,
  refetchMember
}) => {
  const [preferences, setPreferences] = useState({
    daysAndTimes: {
      workDays: {},
      isAcceptUnder24HrsNotice: false,
      preferredWeeklyHours: "0-12"
    },
    travel: {
      maxDistance: 0,
      startingLocation: { lat: null, lng: null, address: "" }
    }
  });

  const [selectedDays, setSelectedDays] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({
    message: "",
    success: true,
    visible: false
  });

  // Parse preferences from member data when component mounts
  useEffect(() => {
    if (member?.memberPreferences?.preferences) {
      try {
        const parsedPrefs =
          typeof member.memberPreferences.preferences === "string"
            ? JSON.parse(member.memberPreferences.preferences)
            : member.memberPreferences.preferences;

        setPreferences(parsedPrefs);

        // Extract selected days for UI display
        const workDays = parsedPrefs?.daysAndTimes?.workDays || {};
        setSelectedDays(
          Object.keys(workDays).reduce((acc, day) => {
            acc[day] = true;
            return acc;
          }, {})
        );
      } catch (error) {
        console.error("Error parsing member preferences:", error);
        setStatus({
          message: "Error loading preferences. Please try again.",
          success: false,
          visible: true
        });
      }
    }
  }, [member]);

  const toggleDay = day => {
    const newSelectedDays = { ...selectedDays };
    const isCurrentlySelected = !!newSelectedDays[day];

    if (isCurrentlySelected) {
      // Remove day if it's already selected
      delete newSelectedDays[day];

      // Also remove from workDays in preferences
      const newWorkDays = { ...preferences.daysAndTimes.workDays };
      delete newWorkDays[day];

      setPreferences({
        ...preferences,
        daysAndTimes: {
          ...preferences.daysAndTimes,
          workDays: newWorkDays
        }
      });
    } else {
      // Add day with default type (DAY)
      newSelectedDays[day] = true;

      setPreferences({
        ...preferences,
        daysAndTimes: {
          ...preferences.daysAndTimes,
          workDays: {
            ...preferences.daysAndTimes.workDays,
            [day]: AVAILABILITY_TYPES.DAY
          }
        }
      });
    }

    setSelectedDays(newSelectedDays);
  };

  const handleDayTypeChange = (day, value) => {
    setPreferences({
      ...preferences,
      daysAndTimes: {
        ...preferences.daysAndTimes,
        workDays: {
          ...preferences.daysAndTimes.workDays,
          [day]: value
        }
      }
    });
  };

  const toggleAcceptShortNotice = () => {
    setPreferences({
      ...preferences,
      daysAndTimes: {
        ...preferences.daysAndTimes,
        isAcceptUnder24HrsNotice: !preferences.daysAndTimes
          .isAcceptUnder24HrsNotice
      }
    });
  };

  const handleWeeklyHoursChange = range => {
    setPreferences({
      ...preferences,
      daysAndTimes: {
        ...preferences.daysAndTimes,
        preferredWeeklyHours: range
      }
    });
  };

  const savePreferences = async () => {
    setIsLoading(true);
    setStatus({ message: "", success: true, visible: false });

    try {
      await updateMemberPreferences(memberId, preferences);

      // Refresh member data to get updated preferences
      if (refetchMember) {
        await refetchMember();
      }

      setStatus({
        message: "Preferences saved successfully!",
        success: true,
        visible: true
      });

      // Hide status message after 3 seconds
      setTimeout(() => {
        setStatus(prev => ({ ...prev, visible: false }));
      }, 3000);
    } catch (error) {
      console.error("Error saving preferences:", error);
      setStatus({
        message: "Error saving preferences. Please try again.",
        success: false,
        visible: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Section>
        <SectionTitle>Preferred work days</SectionTitle>
        <DaySelector
          selectedDays={selectedDays}
          workDays={preferences.daysAndTimes.workDays}
          onToggleDay={toggleDay}
          onDayTypeChange={handleDayTypeChange}
        />
      </Section>

      <Section>
        <ToggleOption
          label="Accept shifts under 24 hrs notice"
          isChecked={preferences.daysAndTimes.isAcceptUnder24HrsNotice}
          onToggle={toggleAcceptShortNotice}
        />
      </Section>

      <Section>
        <SectionTitle>Preferred weekly hours</SectionTitle>
        <WeeklyHours
          selectedHours={preferences.daysAndTimes.preferredWeeklyHours}
          onHoursChange={handleWeeklyHoursChange}
        />
      </Section>

      <ButtonContainer>
        <RotaButton onClick={savePreferences} isDisabled={isLoading}>
          {isLoading ? "Saving..." : "Save Preferences"}
        </RotaButton>
      </ButtonContainer>

      <StatusMessage success={status.success} visible={status.visible}>
        {status.message}
      </StatusMessage>
    </Container>
  );
};

export default flow(updateMemberPreferences)(MemberPreferencesComponent);
